import env from "../../../env";
import Modal from "../../Modal/Modal";
import { loadStripe } from "@stripe/stripe-js";
import useAuthService from "../../../auth/AuthService";
import { useCallback, useEffect, useState } from "react";
import {
  CreateCheckoutSession,
  UpdateCheckoutSession,
} from "../../../services/cart.service";
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from "@stripe/react-stripe-js";
import { useNavigate } from "react-router-dom";
import { handleMessage } from "../../../utils/handleMessagePopup";

const stripePromise = loadStripe(env.STRIPE_PUBLISHABLE_KEY);

export const StripeCheckoutForm: React.FC<{
  onClose?: () => void;
  onClick?: () => void;
  isVisible?: boolean;
  itemIds: any;
}> = ({ onClose, isVisible, itemIds, onClick }) => {
  const { getToken } = useAuthService();
  const navigate = useNavigate();
  const [state, setState] = useState({
    isLoading: false,
    timeRemaining: { minutes: "00", seconds: "00" },
    time_interval: 0,
    start_time: "",
    expire_time: "",
    sessionID: "",
    client_secret: "",
    error: "",
  });

  useEffect(() => {
    if (!state.expire_time) return;
    const intervalId = setInterval(() => {
      const distance = state.time_interval;

      if (distance <= 0) {
        clearInterval(intervalId);
        onClose?.();
        handleMessage("Checkout Session has been Expired")
        setState((prev) => ({
          ...prev,
          timeRemaining: {
            minutes: "00",
            seconds: "00",
          },
        }));
      } else {
        const minutes = Math.floor(distance / 60)
          .toString()
          .padStart(2, "0");
        const seconds = Math.floor(distance % 60)
          .toString()
          .padStart(2, "0");

        setState((prev) => ({
          ...prev,
          timeRemaining: { minutes, seconds },
          time_interval: state.time_interval - 1,
        }));
      }
    }, 1000);

    return () => clearInterval(intervalId);
    //eslint-disable-next-line
  }, [state.time_interval]);

  useEffect(() => {
    const fetchData = async () => {
      if (!isVisible || itemIds.length === 0) return;

      try {
        setState((prev) => ({
          ...prev,
          isLoading: true,
        }));

        const res = await CreateCheckoutSession(
          { product_id: itemIds },
          getToken
        );
        if (res.error) {
          handleMessage(res.error.data);
          onClose?.();
        }

        const startDate = new Date(res.data.data.start_time).getTime();
        const targetDate = new Date(res.data.data.expire_time).getTime();
        setState((prev) => ({
          ...prev,
          client_secret: res.data.data.client_secret,
          sessionID: res.data.data.session_id,
          start_time: res.data.data.start_time,
          expire_time: res.data.data.expire_time,
          time_interval: (targetDate - startDate) / 1000,
        }));
      } catch (error) {
        console.error("Error fetching client secret:", error);
      } finally {
        setState((prev) => ({
          ...prev,
          isLoading: false,
        }));
      }
    };
    fetchData();
    //eslint-disable-next-line
  }, []);

  const handleClose = async () => {
    try {
      await UpdateCheckoutSession({ entity_id: itemIds }, getToken);
      onClose?.();
    } catch (error) {
      console.error("Error fetching client secret:", error);
    }
  };

  const onComplete = useCallback(() => {
    onClick?.();
    navigate("/memberships");
    //eslint-disable-next-line
  }, []);

  const options = {
    clientSecret: state.client_secret,
    onComplete,
  };

  return (
    <Modal
      id="CheckoutForm"
      modalClassName="modal-xl"
      isVisible={isVisible}
      body={
        <div>
          {state.isLoading ? (
            <div className="loader">
              <div className="spinner"></div>
            </div>
          ) : (
            <div className="cart overflow-hidden">
              <div className="cross-button m-auto checkout-heading" onClick={handleClose}>
                <svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M33.5254 33.2754C32.8926 33.9082 31.7324 33.9082 31.0996 33.2754L17.1777 19.248L3.15039 33.2754C2.51758 33.9082 1.35742 33.9082 0.724609 33.2754C0.0917969 32.6426 0.0917969 31.4824 0.724609 30.8496L14.752 16.8223L0.724609 2.90039C0.0917969 2.26758 0.0917969 1.10742 0.724609 0.474609C1.35742 -0.158203 2.51758 -0.158203 3.15039 0.474609L17.1777 14.502L31.0996 0.474609C31.7324 -0.158203 32.8926 -0.158203 33.5254 0.474609C34.1582 1.10742 34.1582 2.26758 33.5254 2.90039L19.498 16.8223L33.5254 30.8496C34.1582 31.4824 34.1582 32.6426 33.5254 33.2754Z" fill="white" />
                </svg>
              </div>

              <div className="w-100 m-30">
                <div className="checkout-heading d-md-flex justify-content-between align-items-baseline m-auto ">
                  <h2 className="header mb-0">Checkout</h2>
                  {state.sessionID && (
                    <h4 className="font-500-20">Checkout expires in {" "}
                      <span className="timer-block">
                        {state.timeRemaining.minutes}m {" "}
                      </span>
                      <span className="timer-block">
                        {state.timeRemaining.seconds}s
                      </span>
                    </h4>
                  )}
                </div>
              </div>
              <div id="checkout">
                <EmbeddedCheckoutProvider
                  stripe={stripePromise}
                  options={options}
                >
                  <EmbeddedCheckout className="checkout-stripe" />
                </EmbeddedCheckoutProvider>
              </div>
            </div>
          )}
        </div>
      }
    />
  );
};
